import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, InputAdornment, LinearProgress, Paper, Stack, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material'
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, StopCircleOutlined as StopIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import PageHeader from '../components/ui/PageHeader'
import { usePermissions } from '../hooks/usePermissions'
import { productService } from '../services/productService'
import {
  ClearanceInput, ClearanceOffer, fromLocalInput, promotionService, STATE_COLOR, toLocalInput,
} from '../services/promotionService'
import { getErrorMessage } from '../utils/errorHandlers'
import { Product } from '../types/product'

const inr = (n?: number | null) => (n == null ? '-' : `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`)
const when = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })

const STATE_TABS = [
  { value: 'live', label: 'Live' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'ended', label: 'Ended' },
  { value: 'paused', label: 'Paused' },
  { value: '', label: 'All' },
]

interface FormState {
  product: Product | null
  discountType: 'percent' | 'fixed_price'
  discountValue: string
  startsAt: string
  endsAt: string
  maxQuantity: string
  badge: string
  notes: string
}

const emptyForm = (): FormState => {
  const start = new Date()
  const end = new Date(start.getTime() + 7 * 86400000)
  return {
    product: null, discountType: 'percent', discountValue: '', startsAt: toLocalInput(start.toISOString()),
    endsAt: toLocalInput(end.toISOString()), maxQuantity: '', badge: '', notes: '',
  }
}

const ClearancePage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { hasPermission } = usePermissions()
  const canWrite = hasPermission('promotions.write')

  const [state, setState] = useState('live')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [rows, setRows] = useState<ClearanceOffer[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ClearanceOffer | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm())
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  const [productOptions, setProductOptions] = useState<Product[]>([])
  const [productQuery, setProductQuery] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await promotionService.listClearance({ state: state || undefined, search: search || undefined, page: page + 1, page_size: pageSize })
      setRows(res.items)
      setTotal(res.total)
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to load clearance offers'), { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [state, search, page, pageSize, enqueueSnackbar])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!dialogOpen || productQuery.trim().length < 2) return
    const handle = setTimeout(async () => {
      try {
        const res = await productService.listProducts({ search: productQuery.trim(), per_page: 20 })
        setProductOptions(res.items)
      } catch { /* picker simply stays empty */ }
    }, 300)
    return () => clearTimeout(handle)
  }, [productQuery, dialogOpen])

  const openCreate = () => {
    setEditing(null); setForm(emptyForm()); setFormError(''); setDialogOpen(true)
  }

  const openEdit = (o: ClearanceOffer) => {
    setEditing(o)
    setForm({
      product: o.product ? ({ id: o.product.id, name: o.product.name, item_code: o.product.item_code } as Product) : null,
      discountType: o.discount_type, discountValue: String(o.discount_value),
      startsAt: toLocalInput(o.starts_at), endsAt: toLocalInput(o.ends_at),
      maxQuantity: o.max_quantity ? String(o.max_quantity) : '', badge: o.badge_text || '', notes: o.notes || '',
    })
    setFormError(''); setDialogOpen(true)
  }

  const regularPrice = useMemo(() => {
    if (editing) return editing.regular_price
    const p = form.product as (Product & { sale_price?: number }) | null
    return p?.sale_price ? Number(p.sale_price) : null
  }, [form.product, editing])

  const preview = useMemo(() => {
    const v = Number(form.discountValue)
    if (!regularPrice || !v) return null
    const price = form.discountType === 'percent' ? regularPrice * (1 - v / 100) : Math.min(v, regularPrice)
    return { price: Math.max(0, price), off: Math.round((1 - Math.max(0, price) / regularPrice) * 100) }
  }, [regularPrice, form.discountType, form.discountValue])

  const save = async () => {
    setFormError('')
    const value = Number(form.discountValue)
    if (!form.product) return setFormError('Choose a product.')
    if (!value || value <= 0) return setFormError('Enter the discount.')
    if (form.discountType === 'percent' && value >= 100) return setFormError('A percentage discount must be below 100.')
    if (!form.startsAt || !form.endsAt) return setFormError('Set the start and end date and time.')
    if (new Date(form.endsAt) <= new Date(form.startsAt)) return setFormError('The end must be after the start.')
    const payload: ClearanceInput = {
      product_id: form.product.id, discount_type: form.discountType, discount_value: value,
      starts_at: fromLocalInput(form.startsAt), ends_at: fromLocalInput(form.endsAt),
      max_quantity: form.maxQuantity ? Number(form.maxQuantity) : null,
      badge_text: form.badge.trim() || null, notes: form.notes.trim() || null,
    }
    setSaving(true)
    try {
      if (editing) {
        const { product_id: _ignored, ...changes } = payload
        await promotionService.updateClearance(editing.id, changes)
      } else {
        await promotionService.createClearance(payload)
      }
      enqueueSnackbar(editing ? 'Clearance offer updated' : 'Clearance offer created', { variant: 'success' })
      setDialogOpen(false)
      load()
    } catch (err) {
      setFormError(getErrorMessage(err, 'Could not save the offer'))
    } finally {
      setSaving(false)
    }
  }

  const act = async (fn: () => Promise<unknown>, done: string) => {
    try { await fn(); enqueueSnackbar(done, { variant: 'success' }); load() }
    catch (err) { enqueueSnackbar(getErrorMessage(err, 'Action failed'), { variant: 'error' }) }
  }

  return (
    <Box>
      <PageHeader
        title="Clearance Sale"
        subtitle="Put products on clearance at a special price for a fixed period. They go live and end automatically."
        breadcrumbs={[{ label: 'Promotions' }, { label: 'Clearance' }]}
        actions={canWrite ? [{ label: 'New clearance offer', onClick: openCreate, icon: <AddIcon />, variant: 'contained' }] : []}
      />

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <Tabs value={state} onChange={(_, v) => { setState(v); setPage(0) }} variant="scrollable" scrollButtons="auto">
              {STATE_TABS.map((t) => <Tab key={t.label} value={t.value} label={t.label} />)}
            </Tabs>
            <TextField size="small" placeholder="Search product or item code" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0) }} sx={{ minWidth: 260 }} />
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Regular</TableCell>
                  <TableCell align="right">Clearance price</TableCell>
                  <TableCell>Runs</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Sold</TableCell>
                  <TableCell>Status</TableCell>
                  {canWrite && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={22} /></TableCell></TableRow>}
                {!loading && rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No clearance offers here yet.{canWrite ? ' Use “New clearance offer” to add one.' : ''}
                  </TableCell></TableRow>
                )}
                {rows.map((o) => (
                  <TableRow key={o.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar variant="rounded" src={o.product?.image || undefined} sx={{ width: 40, height: 40 }}>{o.product?.name?.[0]}</Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{o.product?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{o.product?.item_code}{o.badge_text ? ` · ${o.badge_text}` : ''}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>{inr(o.regular_price)}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color="error.main">{inr(o.clearance_price)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {o.discount_type === 'percent' ? `${o.discount_value}% off` : 'fixed price'}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption" display="block">{when(o.starts_at)}</Typography><Typography variant="caption" display="block">to {when(o.ends_at)}</Typography></TableCell>
                    <TableCell>
                      {o.max_quantity ? (
                        <>
                          <LinearProgress variant="determinate" value={Math.min(100, (o.sold_quantity / o.max_quantity) * 100)} sx={{ height: 6, borderRadius: 3 }} />
                          <Typography variant="caption">{o.sold_quantity} of {o.max_quantity} sold</Typography>
                        </>
                      ) : <Typography variant="caption">{o.sold_quantity} sold · no cap</Typography>}
                    </TableCell>
                    <TableCell><Chip size="small" label={o.state.replace('_', ' ')} color={STATE_COLOR[o.state]} /></TableCell>
                    {canWrite && (
                      <TableCell align="right">
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(o)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        {(o.state === 'live' || o.state === 'scheduled') && (
                          <Tooltip title="Stop now">
                            <IconButton size="small" color="warning" onClick={() => window.confirm('Stop this clearance offer now? The price returns to normal immediately.') && act(() => promotionService.stopClearance(o.id), 'Offer stopped')}>
                              <StopIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {o.sold_quantity === 0 && (
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => window.confirm('Delete this offer?') && act(() => promotionService.deleteClearance(o.id), 'Offer deleted')}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
              onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0) }}
              rowsPerPageOptions={[10, 25, 50]} />
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit clearance offer' : 'New clearance offer'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {formError && <Grid item xs={12}><Alert severity="error">{formError}</Alert></Grid>}
            <Grid item xs={12}>
              <Autocomplete
                disabled={!!editing}
                options={productOptions}
                value={form.product}
                onChange={(_, v) => setForm({ ...form, product: v })}
                onInputChange={(_, v) => setProductQuery(v)}
                getOptionLabel={(p) => `${p.name} (${p.item_code})`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(x) => x}
                noOptionsText={productQuery.length < 2 ? 'Type at least 2 letters' : 'No products found'}
                renderInput={(params) => <TextField {...params} label="Product" helperText="Applies to all variants of the product" />}
              />
            </Grid>
            <Grid item xs={12}>
              <ToggleButtonGroup exclusive size="small" value={form.discountType} onChange={(_, v) => v && setForm({ ...form, discountType: v })}>
                <ToggleButton value="percent">% off</ToggleButton>
                <ToggleButton value="fixed_price">Fixed clearance price</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label={form.discountType === 'percent' ? 'Discount' : 'Clearance price per unit'}
                value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">{form.discountType === 'percent' ? '%' : '₹'}</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Quantity cap (optional)" value={form.maxQuantity}
                onChange={(e) => setForm({ ...form, maxQuantity: e.target.value })} helperText="Ends early when this many units are sold" />
            </Grid>
            {preview && (
              <Grid item xs={12}><Alert severity="info">Customers pay <b>{inr(preview.price)}</b> instead of {inr(regularPrice)} ({preview.off}% off). Customer-specific pricing is discounted from their own price.</Alert></Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="datetime-local" label="Starts" InputLabelProps={{ shrink: true }}
                value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="datetime-local" label="Ends" InputLabelProps={{ shrink: true }}
                value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Badge text (optional)" placeholder="Clearance" inputProps={{ maxLength: 60 }}
                value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Internal note (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Create offer'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ClearancePage
